import { useCallback, useMemo } from 'react'
import { Ban, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchOAuthApps, revokeOAuthApp, DEV_STR as S, type OAuthAppRow } from './devApi'

/**
 * A13.18 — OAuth-приложения. Revoke a registered OAuth app. Dormant until
 * `/admin/developers/oauth` ships. Placeholder permission USERS_VIEW.
 */
export function OAuthAppsScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<OAuthAppRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'client_id', header: S.clientId, type: 'text' },
      { key: 'owner', header: S.owner, type: 'text' },
      { key: 'scopes', header: S.scopes, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<OAuthAppRow>[]>(
    () => [
      {
        id: 'revoke',
        label: S.revokeApp,
        icon: <Ban size={14} />,
        danger: true,
        visible: (r) => r.status === 'active',
        onSelect: async (r) => {
          try {
            await revokeOAuthApp(r.id)
            toast(S.appRevoked, { tone: 'success' })
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchOAuthApps, [])
  return (
    <>
      <PageHeader title={S.oauth} screenId="A13.18" />
      <DataTable<OAuthAppRow>
        screenId="A13.18"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <ShieldCheck size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
